import { useContext, useState, useCallback } from 'react';
import { CommandBatch } from 'commands/Commands';
import { BusinessAddress, BusinessDetails } from 'models/business';
import { ChangeAddressVisibility } from 'commands/business/addresses/ChangeAddressVisibility';
import { ChangeAddressOrder } from 'commands/business/addresses/ChangeAddressOrder';
import { EditContext } from 'context/EditContext';

import BusinessBoard from './BusinessBoard';
import BusinessAddressEditDrawer from './Addresses/BusinessAddressEditDrawer';
import AddressRow from './Addresses/AddressRow';

import { EyeSvg, ArrowUp, ArrowDown, PencilSvg, PlusSvg, LockSvg, MapPinSvg, MapSvg, EmailAtSignSvg, PhoneSvg } from 'components/Svg';

type Props = {
  business: BusinessDetails;
  commands: CommandBatch;
};

const MyCrmInfo = ({ business, commands }: Props) => {
  const [addresses, setAddresses] = useState<BusinessAddress[]>(() => business.details.addresses);
  const [editingAddress, setEditingAddress] = useState<BusinessAddress | null>(null);
  const [addingAddress, setAddingAddress] = useState(false);
  const { setDirty } = useContext(EditContext);

  const toggleVisibility = useCallback(
    (addr: BusinessAddress) => {
      setDirty(true);
      setAddresses(prev =>
        prev.map(a => {
          if (a.id !== addr.id) return a;
          const updated = { ...a, isVisible: !a.isVisible };
          commands.add(new ChangeAddressVisibility(updated.isVisible ?? false, updated.id));
          return updated;
        })
      );
    },
    [commands, setDirty]
  );

  const moveAddress = useCallback(
    (index: number, direction: 'up' | 'down') => {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= addresses.length) return;

      setDirty(true);
      setAddresses(prev => {
        const updated = [...prev];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];

        commands.add(new ChangeAddressOrder(newIndex.toString(), updated[newIndex].id));
        commands.add(new ChangeAddressOrder(index.toString(), updated[index].id));

        return updated;
      });
    },
    [addresses.length, commands, setDirty]
  );

  return (
    <>
      <BusinessBoard
        heading='Business Details'
        bgColor='bg-gray-50'
        action={
          <button
            className={`flex items-center gap-2 text-white text-sm font-medium px-3 py-2 rounded-lg ${
              addresses.length >= 9 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-crm'
            }`}
            onClick={() => setAddingAddress(true)}
            disabled={addresses.length >= 9}
            aria-label='Add new address'
          >
            <PlusSvg className='w-4 h-4' />
            New Address
          </button>
        }
      >
        {addresses.map((addr, index) => (
          <div key={addr.id} className='mb-6 p-4 border-b'>
            <div className='mb-5'>
              <div className='flex items-center justify-between mb-2'>
                <div className='flex items-center gap-5'>
                  <div className='flex items-center gap-1 min-w-[80px]'>
                    <button className='text-blue-crm text-xl' onClick={() => toggleVisibility(addr)} aria-label='Toggle visibility'>
                      <EyeSvg isHidden={!addr.isVisible} className='w-5 h-5' />
                    </button>

                    {index < addresses.length - 1 && (
                      <button onClick={() => moveAddress(index, 'down')} aria-label='Move address down'>
                        <ArrowDown />
                      </button>
                    )}
                    {index > 0 && (
                      <button onClick={() => moveAddress(index, 'up')} aria-label='Move address up'>
                        <ArrowUp />
                      </button>
                    )}
                  </div>
                  <h2 className='font-semibold text-xl text-navy'>{addr.displayName || 'Business display name'}</h2>
                </div>

                {!addr.isDefault && (
                  <button onClick={() => setEditingAddress(addr)} aria-label='Edit address'>
                    <PencilSvg className='text-blue-crm w-6 h-6' />
                  </button>
                )}
              </div>

              {addr.isDefault && (
                <div className='flex items-center overflow-hidden gap-2 p-3 ml-20 font-normal text-xs'>
                  <div className='flex items-center gap-2 p-2 bg-blue-tint border rounded-md border-gray-200 text-xs text-gray-500'>
                    <LockSvg />
                    <span>The details in this entry can be edited in MyCRM and will sync across to your website accordingly</span>
                  </div>
                  <div className='flex items-center gap-1 p-2 border rounded-md bg-cyan-default'>
                    <span className='text-gray-dark'>#</span>
                    <span className='text-gray-light'>My CRM ID</span>
                    <span className='text-blue-crm'>{business.details?.myCrmId}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='pl-[100px]'>
              <AddressRow icon={<MapPinSvg />} label='Address' value={addr.addressLine1 || addr.addressLine2} />
              <AddressRow icon={<MapSvg />} label='Directions' value={addr.directions} isLink />
              <AddressRow icon={<EmailAtSignSvg />} label='Office Email' value={addr.officeEmail} isLink />
              <AddressRow icon={<PhoneSvg />} label='Office Phone Number' value={addr.phoneNumber} isLink />
            </div>
          </div>
        ))}
      </BusinessBoard>

      {editingAddress && (
        <BusinessAddressEditDrawer
          mode='edit'
          selectedAddress={editingAddress}
          toggleHandler={() => setEditingAddress(null)}
          onSave={updatedAddress => {
            setAddresses(prev => prev.map(a => (a.id === updatedAddress.id ? { ...a, ...updatedAddress } : a)));
          }}
          onDelete={addrId => {
            setAddresses(prev => prev.filter(a => a.id !== addrId));
          }}
        />
      )}

      {addingAddress && (
        <BusinessAddressEditDrawer
          mode='add'
          addressesCount={addresses.length}
          toggleHandler={() => setAddingAddress(false)}
          onSave={newAddress => {
            setAddresses(prev => [...prev, newAddress]);
          }}
        />
      )}
    </>
  );
};

export default MyCrmInfo;
